import { orderTypeConstants } from "constants/order";
import dayjs from "dayjs";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { orderTypes } from "pages/CreateOrder/components/Header/constants";
import {
  DEFAULT_SECOND_MESSAGE_HOUR_CST,
  DEFAULT_THIRD_MESSAGE_HOUR_CST,
  DEFAULT_FUTURE_DOS_SECOND_MESSAGE_DAYS,
  DEFAULT_FUTURE_DOS_THIRD_MESSAGE_DAYS,
  DEFAULT_INTEGRATION_SECOND_MESSAGE_DAYS,
  DEFAULT_INTEGRATION_THIRD_MESSAGE_DAYS,
  DEFAULT_PAST_DOS_SECOND_MESSAGE_DAYS,
  DEFAULT_PAST_DOS_THIRD_MESSAGE_DAYS,
} from "pages/HospitalsPage/constants/communicationScheduleDefaults";
import { formValidationConstants } from "pages/HospitalsPage/helpers/formValidations";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useListModelsQuery } from "store/queries/chat";
import styled from "styled-components";

const MIN_MESSAGE_HOUR_CST = 1;

const MESSAGE_SCHEDULE_MIN_DATE = new Date(
  2000,
  0,
  1,
  MIN_MESSAGE_HOUR_CST,
  0,
  0,
  0
);

function messageHourToDate(hour: number | null | undefined): Date | null {
  if (hour === null || hour === undefined) return null;
  const h = hour === 0 ? MIN_MESSAGE_HOUR_CST : hour;
  const d = new Date(2000, 0, 1, 0, 0, 0, 0);
  d.setHours(h, 0, 0, 0);
  return d;
}

function calendarValueToMessageHour(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const d = value instanceof Date ? new Date(value) : new Date(value as string);
  if (Number.isNaN(d.getTime())) return null;
  d.setMinutes(0, 0, 0);
  const h = d.getHours();
  return h === 0 ? MIN_MESSAGE_HOUR_CST : h;
}

const HourOnlyCalendar = styled(Calendar)`
  width: 100%;
  .p-timepicker .p-hour-picker + .p-separator,
  .p-timepicker .p-minute-picker {
    display: none;
  }
`;

/**
 * Returns true when the 3rd message would be sent before (or at the same
 * time as) the 2nd message.
 *
 * For "beforeDos" sections the timeline is inverted: more days-before-DOS
 * means the message fires earlier, so the 3rd must have *fewer* days.
 *
 * For "afterCreation" sections the timeline is normal: more days means
 * later, so the 3rd must have *more* days.
 */
function isThirdBeforeSecond(
  secondDays: number,
  secondHour: number,
  thirdDays: number,
  thirdHour: number,
  mode: "beforeDos" | "afterCreation"
): boolean {
  const second = mode === "beforeDos" ? -secondDays : secondDays;
  const third = mode === "beforeDos" ? -thirdDays : thirdDays;

  if (third < second) return true;
  if (third === second && thirdHour <= secondHour) return true;
  return false;
}

const SCHEDULE_ORDER_ERROR =
  "3rd message must be scheduled after the 2nd message.";

const { DAYS_REQUIRED } = formValidationConstants.COMMUNICATION_SCHEDULE;

type OrderCommunicationDetailsFields =
  | "messageSender"
  | "emailSender"
  | "emailTemplateAttempt1"
  | "attempt1"
  | "emailTemplateAttempt2"
  | "attempt2"
  | "emailTemplateAttempt3"
  | "attempt3"
  | "emailTemplateAttemptBundle1"
  | "attemptBundle1"
  | "emailTemplateAttemptBundle2"
  | "attemptBundle2"
  | "emailTemplateAttemptBundle3"
  | "attemptBundle3"
  | "accountLogoUrl"
  | "emailTemplateAttemptED1"
  | "attemptED1"
  | "emailTemplateAttemptED2"
  | "attemptED2"
  | "emailTemplateAttemptED3"
  | "attemptED3"
  | "emailTemplateAttemptBariatrics1"
  | "attemptBariatrics1"
  | "emailTemplateAttemptBariatrics2"
  | "attemptBariatrics2"
  | "emailTemplateAttemptBariatrics3"
  | "attemptBariatrics3"
  | "emailTemplateAttemptGFE1"
  | "attemptGFE1"
  | "emailTemplateAttemptGFE2"
  | "attemptGFE2"
  | "emailTemplateAttemptGFE3"
  | "attemptGFE3"
  | "openAiChatModelId"
  | "futureDosSecondMessageDays"
  | "futureDosThirdMessageDays"
  | "futureDosSecondMessageHourCST"
  | "futureDosThirdMessageHourCST"
  | "integrationSecondMessageDays"
  | "integrationThirdMessageDays"
  | "integrationSecondMessageHourCST"
  | "integrationThirdMessageHourCST"
  | "pastDosSecondMessageDays"
  | "pastDosThirdMessageDays"
  | "pastDosSecondMessageHourCST"
  | "pastDosThirdMessageHourCST";

export function OrderCommunicationDetails() {
  const [expanded, setExpanded] = useState(false);
  const {
    control,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext<any>();
  const orderType = watch("orderType");
  const { data: chatModels, isLoading: isLoadingModels } = useListModelsQuery();
  const { getFormErrorMessage } = useFormValidation();

  const futureDos2Days = watch("futureDosSecondMessageDays");
  const futureDos2Hour = watch("futureDosSecondMessageHourCST");
  const futureDos3Days = watch("futureDosThirdMessageDays");
  const futureDos3Hour = watch("futureDosThirdMessageHourCST");

  const integration2Days = watch("integrationSecondMessageDays");
  const integration2Hour = watch("integrationSecondMessageHourCST");
  const integration3Days = watch("integrationThirdMessageDays");
  const integration3Hour = watch("integrationThirdMessageHourCST");

  const pastDos2Days = watch("pastDosSecondMessageDays");
  const pastDos2Hour = watch("pastDosSecondMessageHourCST");
  const pastDos3Days = watch("pastDosThirdMessageDays");
  const pastDos3Hour = watch("pastDosThirdMessageHourCST");

  const futureDosError = useMemo(
    () =>
      isThirdBeforeSecond(
        futureDos2Days ?? DEFAULT_FUTURE_DOS_SECOND_MESSAGE_DAYS,
        futureDos2Hour ?? DEFAULT_SECOND_MESSAGE_HOUR_CST,
        futureDos3Days ?? DEFAULT_FUTURE_DOS_THIRD_MESSAGE_DAYS,
        futureDos3Hour ?? DEFAULT_THIRD_MESSAGE_HOUR_CST,
        "beforeDos"
      ),
    [futureDos2Days, futureDos2Hour, futureDos3Days, futureDos3Hour]
  );

  const integrationError = useMemo(
    () =>
      isThirdBeforeSecond(
        integration2Days ?? DEFAULT_INTEGRATION_SECOND_MESSAGE_DAYS,
        integration2Hour ?? DEFAULT_SECOND_MESSAGE_HOUR_CST,
        integration3Days ?? DEFAULT_INTEGRATION_THIRD_MESSAGE_DAYS,
        integration3Hour ?? DEFAULT_THIRD_MESSAGE_HOUR_CST,
        "afterCreation"
      ),
    [integration2Days, integration2Hour, integration3Days, integration3Hour]
  );

  const pastDosError = useMemo(
    () =>
      isThirdBeforeSecond(
        pastDos2Days ?? DEFAULT_PAST_DOS_SECOND_MESSAGE_DAYS,
        pastDos2Hour ?? DEFAULT_SECOND_MESSAGE_HOUR_CST,
        pastDos3Days ?? DEFAULT_PAST_DOS_THIRD_MESSAGE_DAYS,
        pastDos3Hour ?? DEFAULT_THIRD_MESSAGE_HOUR_CST,
        "afterCreation"
      ),
    [pastDos2Days, pastDos2Hour, pastDos3Days, pastDos3Hour]
  );

  const validateFutureDosThird = useCallback(
    () => !futureDosError || SCHEDULE_ORDER_ERROR,
    [futureDosError]
  );
  const validateIntegrationThird = useCallback(
    () => !integrationError || SCHEDULE_ORDER_ERROR,
    [integrationError]
  );
  const validatePastDosThird = useCallback(
    () => !pastDosError || SCHEDULE_ORDER_ERROR,
    [pastDosError]
  );

  useEffect(() => {
    trigger("futureDosThirdMessageDays");
  }, [futureDosError, trigger]);

  useEffect(() => {
    trigger("integrationThirdMessageDays");
  }, [integrationError, trigger]);

  useEffect(() => {
    trigger("pastDosThirdMessageDays");
  }, [pastDosError, trigger]);

  async function handleOnBlurField({
    onBlur,
    field,
  }: {
    onBlur: () => void;
    field: OrderCommunicationDetailsFields;
  }) {
    trigger(field);
    onBlur();
  }

  return (
    <div className="block bg-white border-round-xl p-4 border-1 border-gray-300">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">
          Order Communication Details
        </span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="flex flex-column gap-4 mt-4">
          <div className="formgrid grid border-bottom-1 border-gray-200">
            <div className="field md:col-4 col-12">
              <Label htmlFor="messageSender">Message Sender</Label>
              <Controller
                name="messageSender"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputText
                    className="w-full"
                    id="messageSender"
                    autoComplete="off"
                    value={value || ""}
                    placeholder="Enter message sender here"
                    aria-labelledby="messageSender"
                    onBlur={() => {
                      handleOnBlurField({ onBlur, field: "messageSender" });
                    }}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <div className="field md:col-4 col-12">
              <Label htmlFor="emailSender">Email sender</Label>
              <Controller
                name="emailSender"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputText
                    className="w-full"
                    id="emailSender"
                    autoComplete="off"
                    value={value || ""}
                    placeholder="Enter email sender here"
                    aria-labelledby="emailSender"
                    onBlur={() => {
                      handleOnBlurField({ onBlur, field: "emailSender" });
                    }}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <div className="field md:col-4 col-12">
              <Label htmlFor="orderType">Order Type</Label>
              <Controller
                name="orderType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="orderType"
                    data-testid="orderType"
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    options={Object.values(orderTypes)}
                    optionLabel="name"
                    placeholder="Select"
                    className="w-full"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex flex-column border-bottom-1 border-gray-200 pb-2">
            <span className="font-medium text-md">Mobile Alert Messages</span>
            {orderType === orderTypeConstants.PATIENT_RESPONSIBILITY && (
              <>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attempt1">Attempt 1 Message</Label>
                  <Controller
                    name="attempt1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attempt1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attempt1"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attempt1" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attempt2">Attempt 2 Message</Label>
                  <Controller
                    name="attempt2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attempt2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attempt2"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attempt2" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attempt3">Attempt 3 Message</Label>
                  <Controller
                    name="attempt3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attempt3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attempt3"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attempt3" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </>
            )}
            {orderType === orderTypeConstants.ED && (
              <>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptED1">Attempt 1 Message</Label>
                  <Controller
                    name="attemptED1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptED1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptED1"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attemptED1" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptED2">Attempt 2 Message</Label>
                  <Controller
                    name="attemptED2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptED2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptED2"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attemptED2" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptED3">Attempt 3 Message</Label>
                  <Controller
                    name="attemptED3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptED3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptED3"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attemptED3" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </>
            )}

            {orderType === orderTypeConstants.BUNDLED && (
              <>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptBundle1">Attempt 1 Message</Label>
                  <Controller
                    name="attemptBundle1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptBundle1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptBundle1"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "attemptBundle1",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptBundle2">Attempt 2 Message</Label>
                  <Controller
                    name="attemptBundle2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptBundle2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptBundle2"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "attemptBundle2",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptBundle3">Attempt 3 Message</Label>
                  <Controller
                    name="attemptBundle3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptBundle3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptBundle3"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "attemptBundle3",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </>
            )}

            {orderType === orderTypeConstants.BARIATRICS && (
              <>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptBariatrics1">Attempt 1 Message</Label>
                  <Controller
                    name="attemptBariatrics1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptBariatrics1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptBariatrics1"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "attemptBariatrics1",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptBariatrics2">Attempt 2 Message</Label>
                  <Controller
                    name="attemptBariatrics2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptBariatrics2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptBariatrics2"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "attemptBariatrics2",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptBariatrics3">Attempt 3 Message</Label>
                  <Controller
                    name="attemptBariatrics3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptBariatrics3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptBariatrics3"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "attemptBariatrics3",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </>
            )}
            {orderType === orderTypeConstants.GFE && (
              <>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptGFE1">Attempt 1 Message</Label>
                  <Controller
                    name="attemptGFE1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptGFE1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptGFE1"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attemptGFE1" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptGFE2">Attempt 2 Message</Label>
                  <Controller
                    name="attemptGFE2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptGFE2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptGFE2"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attemptGFE2" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full flex flex-column gap-2 mt-4">
                  <Label htmlFor="attemptGFE3">Attempt 3 Message</Label>
                  <Controller
                    name="attemptGFE3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputTextarea
                        className="w-full"
                        id="attemptGFE3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="attemptGFE3"
                        rows={3}
                        onBlur={() => {
                          handleOnBlurField({ onBlur, field: "attemptGFE3" });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-column border-bottom-1 border-gray-200 pb-2">
            <span className="font-medium text-md mb-4">
              Email Alert Messages
            </span>
            {orderType === orderTypeConstants.PATIENT_RESPONSIBILITY && (
              <div className="formgrid grid">
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttempt1">
                    Email Template ID: Attempt 1
                  </Label>
                  <Controller
                    name="emailTemplateAttempt1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttempt1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttempt1"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttempt1",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttempt2">
                    Email Template ID: Attempt 2
                  </Label>
                  <Controller
                    name="emailTemplateAttempt2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttempt2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttempt2"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttempt2",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttempt3">
                    Email Template ID: Attempt 3
                  </Label>
                  <Controller
                    name="emailTemplateAttempt3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttempt3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttempt3"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttempt3",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            )}
            {orderType === orderTypeConstants.ED && (
              <div className="formgrid grid">
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptED1">
                    Email Template ID: Attempt 1
                  </Label>
                  <Controller
                    name="emailTemplateAttemptED1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptED1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptED1"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptED1",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptED2">
                    Email Template ID: Attempt 2
                  </Label>
                  <Controller
                    name="emailTemplateAttemptED2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptED2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptED2"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptED2",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptED3">
                    Email Template ID: Attempt 3
                  </Label>
                  <Controller
                    name="emailTemplateAttemptED3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptED3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptED3"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptED3",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            )}

            {orderType === orderTypeConstants.BUNDLED && (
              <div className="formgrid grid">
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptBundle1">
                    Email Template ID: Attempt 1
                  </Label>
                  <Controller
                    name="emailTemplateAttemptBundle1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptBundle1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptBundle1"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptBundle1",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptBundle2">
                    Email Template ID: Attempt 2
                  </Label>
                  <Controller
                    name="emailTemplateAttemptBundle2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptBundle2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptBundle2"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptBundle2",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptBundle3">
                    Email Template ID: Attempt 3
                  </Label>
                  <Controller
                    name="emailTemplateAttemptBundle3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptBundle3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptBundle3"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptBundle3",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            )}

            {/* {orderType === orderTypeConstants.BARIATRICS && (
              <div className="formgrid grid">
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptBariatrics1">
                    Email Template ID: Attempt 1
                  </Label>
                  <Controller
                    name="emailTemplateAttemptBariatrics1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptBariatrics1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptBariatrics1"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptBariatrics1",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptBariatrics2">
                    Email Template ID: Attempt 2
                  </Label>
                  <Controller
                    name="emailTemplateAttemptBariatrics2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptBariatrics2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptBariatrics2"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptBariatrics2",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptBariatrics3">
                    Email Template ID: Attempt 3
                  </Label>
                  <Controller
                    name="emailTemplateAttemptBariatrics3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptBariatrics3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptBariatrics3"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptBariatrics3",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            )} */}
            {orderType === orderTypeConstants.GFE && (
              <div className="formgrid grid">
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptGFE1">
                    Email Template ID: Attempt 1
                  </Label>
                  <Controller
                    name="emailTemplateAttemptGFE1"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptGFE1"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptGFE1"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptGFE1",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptGFE2">
                    Email Template ID: Attempt 2
                  </Label>
                  <Controller
                    name="emailTemplateAttemptGFE2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptGFE2"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptGFE2"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptGFE2",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="field md:col-4 col-12">
                  <Label htmlFor="emailTemplateAttemptGFE3">
                    Email Template ID: Attempt 3
                  </Label>
                  <Controller
                    name="emailTemplateAttemptGFE3"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputText
                        className="w-full"
                        id="emailTemplateAttemptGFE3"
                        autoComplete="off"
                        value={value || ""}
                        placeholder="Enter your message here"
                        aria-labelledby="emailTemplateAttemptGFE3"
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "emailTemplateAttemptGFE3",
                          });
                        }}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-column">
            <span className="font-medium text-md mb-4">
              Communication Schedule
            </span>

            {/* Section 1: DOS > Order Date */}
            <div className="border-1 border-gray-200 border-round p-3 mb-3">
              <span className="font-medium text-sm mb-3 block">
                DOS is after Order Creation Date
              </span>
              <span className="text-sm text-color-secondary block mb-2">
                1st message: Sent immediately upon creation or at the scheduled
                time.
              </span>
              <div className="flex align-items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm">2nd message</span>
                <Controller
                  name="futureDosSecondMessageDays"
                  control={control}
                  rules={{ required: DAYS_REQUIRED }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <InputNumber
                      id="futureDosSecondMessageDays"
                      data-testid="futureDosSecondMessageDays"
                      value={value}
                      onValueChange={(e) => onChange(e.value)}
                      min={0}
                      style={{ width: "4rem" }}
                      inputStyle={{ width: "4rem", textAlign: "center" }}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "futureDosSecondMessageDays",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">days before DOS</span>
                <Controller
                  name="futureDosSecondMessageHourCST"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <HourOnlyCalendar
                      id="futureDosSecondMessageHourCST"
                      inputId="futureDosSecondMessageHourCST"
                      data-testid="futureDosSecondMessageHourCST"
                      value={messageHourToDate(
                        value ?? DEFAULT_SECOND_MESSAGE_HOUR_CST
                      )}
                      onChange={(e) =>
                        onChange(
                          calendarValueToMessageHour(e.value) ??
                            DEFAULT_SECOND_MESSAGE_HOUR_CST
                        )
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={60}
                      minDate={MESSAGE_SCHEDULE_MIN_DATE}
                      showIcon
                      placeholder="Time"
                      style={{ width: "9rem" }}
                      formatDateTime={(d) => dayjs(d).format("h:mm A")}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "futureDosSecondMessageHourCST",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">CST</span>
              </div>
              {getFormErrorMessage("futureDosSecondMessageDays", errors)}
              <div className="flex align-items-center gap-2 flex-wrap">
                <span className="text-sm">3rd message</span>
                <Controller
                  name="futureDosThirdMessageDays"
                  control={control}
                  rules={{
                    required: DAYS_REQUIRED,
                    validate: validateFutureDosThird,
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <InputNumber
                      id="futureDosThirdMessageDays"
                      data-testid="futureDosThirdMessageDays"
                      value={value}
                      onValueChange={(e) => onChange(e.value)}
                      min={0}
                      style={{ width: "4rem" }}
                      inputStyle={{ width: "4rem", textAlign: "center" }}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "futureDosThirdMessageDays",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">days before DOS</span>
                <Controller
                  name="futureDosThirdMessageHourCST"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <HourOnlyCalendar
                      id="futureDosThirdMessageHourCST"
                      inputId="futureDosThirdMessageHourCST"
                      data-testid="futureDosThirdMessageHourCST"
                      value={messageHourToDate(
                        value ?? DEFAULT_THIRD_MESSAGE_HOUR_CST
                      )}
                      onChange={(e) =>
                        onChange(
                          calendarValueToMessageHour(e.value) ??
                            DEFAULT_THIRD_MESSAGE_HOUR_CST
                        )
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={60}
                      minDate={MESSAGE_SCHEDULE_MIN_DATE}
                      showIcon
                      placeholder="Time"
                      style={{ width: "9rem" }}
                      formatDateTime={(d) => dayjs(d).format("h:mm A")}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "futureDosThirdMessageHourCST",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">CST</span>
              </div>
              {getFormErrorMessage("futureDosThirdMessageDays", errors)}
            </div>

            {/* Section 2: Orders from import tables (DOS < Order Date) */}
            <div className="border-1 border-gray-200 border-round p-3 mb-3">
              <span className="font-medium text-sm mb-3 block">
                Orders from import tables (DOS is before Order Creation Date)
              </span>
              <span className="text-sm text-color-secondary block mb-2">
                1st message: Sent immediately upon creation or at the scheduled
                time.
              </span>
              <div className="flex align-items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm">2nd message</span>
                <Controller
                  name="integrationSecondMessageDays"
                  control={control}
                  rules={{ required: DAYS_REQUIRED }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <InputNumber
                      id="integrationSecondMessageDays"
                      data-testid="integrationSecondMessageDays"
                      value={value}
                      onValueChange={(e) => onChange(e.value)}
                      min={0}
                      style={{ width: "4rem" }}
                      inputStyle={{ width: "4rem", textAlign: "center" }}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "integrationSecondMessageDays",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">days after Order Creation Date</span>
                <Controller
                  name="integrationSecondMessageHourCST"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <HourOnlyCalendar
                      id="integrationSecondMessageHourCST"
                      inputId="integrationSecondMessageHourCST"
                      data-testid="integrationSecondMessageHourCST"
                      value={messageHourToDate(
                        value ?? DEFAULT_SECOND_MESSAGE_HOUR_CST
                      )}
                      onChange={(e) =>
                        onChange(
                          calendarValueToMessageHour(e.value) ??
                            DEFAULT_SECOND_MESSAGE_HOUR_CST
                        )
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={60}
                      minDate={MESSAGE_SCHEDULE_MIN_DATE}
                      showIcon
                      placeholder="Time"
                      style={{ width: "9rem" }}
                      formatDateTime={(d) => dayjs(d).format("h:mm A")}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "integrationSecondMessageHourCST",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">CST</span>
              </div>
              {getFormErrorMessage("integrationSecondMessageDays", errors)}
              <div className="flex align-items-center gap-2 flex-wrap">
                <span className="text-sm">3rd message</span>
                <Controller
                  name="integrationThirdMessageDays"
                  control={control}
                  rules={{
                    required: DAYS_REQUIRED,
                    validate: validateIntegrationThird,
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <InputNumber
                      id="integrationThirdMessageDays"
                      data-testid="integrationThirdMessageDays"
                      value={value}
                      onValueChange={(e) => onChange(e.value)}
                      min={0}
                      style={{ width: "4rem" }}
                      inputStyle={{ width: "4rem", textAlign: "center" }}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "integrationThirdMessageDays",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">days after Order Creation Date</span>
                <Controller
                  name="integrationThirdMessageHourCST"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <HourOnlyCalendar
                      id="integrationThirdMessageHourCST"
                      inputId="integrationThirdMessageHourCST"
                      data-testid="integrationThirdMessageHourCST"
                      value={messageHourToDate(
                        value ?? DEFAULT_THIRD_MESSAGE_HOUR_CST
                      )}
                      onChange={(e) =>
                        onChange(
                          calendarValueToMessageHour(e.value) ??
                            DEFAULT_THIRD_MESSAGE_HOUR_CST
                        )
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={60}
                      minDate={MESSAGE_SCHEDULE_MIN_DATE}
                      showIcon
                      placeholder="Time"
                      style={{ width: "9rem" }}
                      formatDateTime={(d) => dayjs(d).format("h:mm A")}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "integrationThirdMessageHourCST",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">CST</span>
              </div>
              {getFormErrorMessage("integrationThirdMessageDays", errors)}
            </div>

            {/* Section 3: DOS < Order Date */}
            <div className="border-1 border-gray-200 border-round p-3">
              <span className="font-medium text-sm mb-3 block">
                DOS is before Order Creation Date
              </span>
              <span className="text-sm text-color-secondary block mb-2">
                1st message: Sent immediately upon creation or at the scheduled
                time.
              </span>
              <div className="flex align-items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm">2nd message</span>
                <Controller
                  name="pastDosSecondMessageDays"
                  control={control}
                  rules={{ required: DAYS_REQUIRED }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <InputNumber
                      id="pastDosSecondMessageDays"
                      data-testid="pastDosSecondMessageDays"
                      value={value}
                      onValueChange={(e) => onChange(e.value)}
                      min={0}
                      style={{ width: "4rem" }}
                      inputStyle={{ width: "4rem", textAlign: "center" }}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "pastDosSecondMessageDays",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">days after Order Creation Date</span>
                <Controller
                  name="pastDosSecondMessageHourCST"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <HourOnlyCalendar
                      id="pastDosSecondMessageHourCST"
                      inputId="pastDosSecondMessageHourCST"
                      data-testid="pastDosSecondMessageHourCST"
                      value={messageHourToDate(
                        value ?? DEFAULT_SECOND_MESSAGE_HOUR_CST
                      )}
                      onChange={(e) =>
                        onChange(
                          calendarValueToMessageHour(e.value) ??
                            DEFAULT_SECOND_MESSAGE_HOUR_CST
                        )
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={60}
                      minDate={MESSAGE_SCHEDULE_MIN_DATE}
                      showIcon
                      placeholder="Time"
                      style={{ width: "9rem" }}
                      formatDateTime={(d) => dayjs(d).format("h:mm A")}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "pastDosSecondMessageHourCST",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">CST</span>
              </div>
              {getFormErrorMessage("pastDosSecondMessageDays", errors)}
              <div className="flex align-items-center gap-2 flex-wrap">
                <span className="text-sm">3rd message</span>
                <Controller
                  name="pastDosThirdMessageDays"
                  control={control}
                  rules={{
                    required: DAYS_REQUIRED,
                    validate: validatePastDosThird,
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <InputNumber
                      id="pastDosThirdMessageDays"
                      data-testid="pastDosThirdMessageDays"
                      value={value}
                      onValueChange={(e) => onChange(e.value)}
                      min={0}
                      style={{ width: "4rem" }}
                      inputStyle={{ width: "4rem", textAlign: "center" }}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "pastDosThirdMessageDays",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">days after Order Creation Date</span>
                <Controller
                  name="pastDosThirdMessageHourCST"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <HourOnlyCalendar
                      id="pastDosThirdMessageHourCST"
                      inputId="pastDosThirdMessageHourCST"
                      data-testid="pastDosThirdMessageHourCST"
                      value={messageHourToDate(
                        value ?? DEFAULT_THIRD_MESSAGE_HOUR_CST
                      )}
                      onChange={(e) =>
                        onChange(
                          calendarValueToMessageHour(e.value) ??
                            DEFAULT_THIRD_MESSAGE_HOUR_CST
                        )
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={60}
                      minDate={MESSAGE_SCHEDULE_MIN_DATE}
                      showIcon
                      placeholder="Time"
                      style={{ width: "9rem" }}
                      formatDateTime={(d) => dayjs(d).format("h:mm A")}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "pastDosThirdMessageHourCST",
                        });
                      }}
                    />
                  )}
                />
                <span className="text-sm">CST</span>
              </div>
              {getFormErrorMessage("pastDosThirdMessageDays", errors)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
